/**
 * The warmup catalog's shape, and the one place it is translated.
 *
 * Pure — no React, no Firestore, no DOM — so the seed script and the portal
 * share exactly one definition and cannot drift.
 *
 * WHY A TRANSLATION EXISTS AT ALL. The handoff says "fields exactly as in the
 * JSON", and for eleven of the thirteen that is what happens. Two cannot be
 * stored as written: **Firestore does not allow nested arrays**, and the
 * catalog's `pattern` is `[["5", 2.0], ["1", 2.0]]` while `glides` is
 * `[[0, 1]]`. The write is rejected outright — `3 INVALID_ARGUMENT: Nested
 * arrays are not allowed` — so this is a database constraint rather than a
 * preference, and it was found by running the seeder rather than by reading.
 *
 * So those two are stored as arrays of MAPS, which Firestore does allow:
 *
 *   pattern  [["5", 2.0]]  ->  [{ deg: "5", beats: 2 }]
 *   glides   [[0, 1]]      ->  [{ from: 0, to: 1 }]
 *
 * and `fromStored` turns them back into the tuple shape on read. That inverse
 * is the point: it lets the Web Audio synth and the pattern SVG stay the
 * PROTOTYPE'S CODE, VERBATIM, indexing `n[0]` / `n[1]` exactly as they do in
 * `docs/refs/warmups/cyv-warmup-catalog-v2-review.html`. Translating at the
 * boundary keeps the translation in one testable function instead of smeared
 * through the two places that are hardest to verify by eye.
 */

/** Group order IS catalog order, and the blurbs are the prototype's own. */
export const PURPOSES = [
	['warm-in', 'Warm-in', 'Semi-occluded and gentle. First sounds of the day.'],
	['breath', 'Breath', 'Support and pulse before any real range work.'],
	['registration', 'Registration', 'Head, chest, mix, and the places they meet.'],
	['resonance', 'Resonance', 'Vowels, ring, and where the sound sits.'],
	['agility', 'Agility', 'Scales and skips — deliberately not only arpeggios.'],
	['style', 'Style', 'Legit, pop, MT belt, and choral gestures.'],
	['diction', 'Diction', 'Tongue-twisters on pitch.'],
	['exploration', 'Games', 'Call and response, counting, sirens with a story. Mostly for 8–13.'],
	['cool-down', 'Cool-down', 'Last sounds of the lesson.'],
];

/** Scale degree → semitones above the tonic. Verbatim from the prototype. */
export const DEG = {
	1: 0, b2: 1, 2: 2, b3: 3, 3: 4, 4: 5, '#4': 6, 5: 7,
	b6: 8, 6: 9, b7: 10, 7: 11, '+8': 12, '+9': 14, '+10': 16, '+12': 19, '+15': 24,
};

export const STYLE_WORDS = { all: 'any style', legit: 'legit', pop: 'pop', belt: 'MT belt', choral: 'choral' };

export const LEVEL_WORDS = { beg: 'beginner and up', int: 'intermediate and up', adv: 'advanced' };

/** The catalog fields carried through verbatim. Listed rather than spread so an
 *  unexpected key is dropped loudly by the seeder instead of silently stored. */
export const PASSTHROUGH_FIELDS = [
	'name', 'purpose', 'syl', 'artic', 'bpm', 'level', 'styles', 'ages', 'notes', 'was', 'echo',
];

export const CATALOG_VERSION = 2;

/** Catalog entry → the Firestore document body (no id; the id is the doc id). */
export const toStored = (entry, index) => {
	const out = { sortKey: index, version: CATALOG_VERSION };
	PASSTHROUGH_FIELDS.forEach((k) => {
		// undefined is not a Firestore value, and an ABSENT optional key should
		// read as an explicit fact rather than a missing field.
		out[k] = entry[k] === undefined ? null : entry[k];
	});
	out.pattern = (entry.pattern || []).map(([deg, beats]) => ({ deg, beats }));
	out.glides = (entry.glides || []).map(([from, to]) => ({ from, to }));
	return out;
};

/** Firestore document (+ its id) → the tuple shape the prototype's code expects. */
export const fromStored = (id, data = {}) => ({
	...data,
	id,
	pattern: (data.pattern || []).map((n) =>
		Array.isArray(n) ? n : [n.deg, n.beats]
	),
	glides: (data.glides || []).map((g) =>
		Array.isArray(g) ? g : [g.from, g.to]
	),
});

/** Multi-syllable exercises carry an array; the row shows the first few. */
export const sylText = (c) =>
	Array.isArray(c.syl) ? c.syl.slice(0, 6).join(' ') + (c.syl.length > 6 ? ' …' : '') : c.syl || '';

/**
 * The prototype's filter, unchanged in behaviour.
 * `ages` is a space-separated list ("11-13 14-18") or the string "all".
 */
export const matchesFilters = (c, { purpose = '', ages = '', q = '' } = {}) => {
	if (purpose && c.purpose !== purpose) return false;
	if (ages && c.ages !== 'all' && !String(c.ages || '').split(' ').includes(ages)) return false;
	if (q) {
		const hay = `${c.id} ${c.name} ${sylText(c)} ${c.notes || ''}`.toLowerCase();
		if (!hay.includes(q.toLowerCase())) return false;
	}
	return true;
};

/** "n of 69 reviewed — k keep, t change, c cut", or the empty state. */
export const progressLine = (reviews, total) => {
	const rows = Object.values(reviews || {});
	const done = rows.filter((r) => r && (r.verdict || (r.note || '').trim())).length;
	if (!done) return 'Nothing reviewed yet';
	const n = (v) => rows.filter((r) => r && r.verdict === v).length;
	return `${done} of ${total} reviewed — ${n('keep')} keep, ${n('tweak')} change, ${n('cut')} cut`;
};

/**
 * The plain-text export, byte-for-byte the prototype's format — this is what
 * lands in Anthony's hands, so the shape is part of the contract.
 *
 * `byReviewer` is optional: with one reviewer the line matches the prototype
 * exactly; with several, the reviewer's name is appended so two verdicts on one
 * exercise stay attributable.
 */
export const exportText = (catalog, reviewsById, { title = "CYV warmup catalog draft 2 — Grace's notes", withNames = false } = {}) => {
	const lines = [title, ''];
	catalog.forEach((c) => {
		const rows = reviewsById[c.id] || [];
		(Array.isArray(rows) ? rows : [rows]).forEach((r) => {
			if (!r || (!r.verdict && !(r.note || '').trim())) return;
			const who = withNames && r.updatedByName ? ` (${r.updatedByName})` : '';
			const verdict = r.verdict ? r.verdict.toUpperCase() : 'note';
			const note = (r.note || '').trim() ? `: ${(r.note || '').trim()}` : '';
			lines.push(`${c.id} ${c.name}${who} — ${verdict}${note}`);
		});
	});
	if (lines.length === 2) lines.push('(no notes yet)');
	return lines.join('\n');
};
